/**
 * 错误日志服务
 *
 * 统一管理错误日志记录、上报和处理
 */

import { ErrorInfo } from 'react';
import report, { ReportEvent } from './report';

export interface ErrorLogEntry {
  message: string;
  stack?: string;
  timestamp: number;
  context?: string;
  componentStack?: string;
  url?: string;
  userAgent?: string;
  userId?: string;
  [key: string]: any;
}

class ErrorLogger {
  private static instance: ErrorLogger;
  private errorQueue: ErrorLogEntry[] = [];
  private maxQueueSize = 50;
  private isProduction = process.env.NODE_ENV === 'production';
  private readonly noisyThirdPartyDomains = [
    'doubleclick.net',
    'googlesyndication.com',
    'googleadservices.com',
    'googletagmanager.com',
  ];

  private constructor() {
    // 初始化全局错误监听
    this.initGlobalErrorHandlers();
  }

  /**
   * 获取单例实例
   */
  public static getInstance(): ErrorLogger {
    if (!ErrorLogger.instance) {
      ErrorLogger.instance = new ErrorLogger();
    }
    return ErrorLogger.instance;
  }

  /**
   * 初始化全局错误处理器
   */
  private initGlobalErrorHandlers() {
    if (typeof window === 'undefined') return;

    // 捕获未处理的 Promise rejection
    window.addEventListener('unhandledrejection', (event) => {
      const normalized = this.normalizeUnhandledRejectionReason(event.reason);

      if (normalized.preventDefault) {
        event.preventDefault();
      }

      if (normalized.ignore) {
        return;
      }

      this.logError({
        message: normalized.message,
        stack: normalized.stack,
        context: 'unhandledrejection',
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        ...normalized.extra,
      });
    });

    // 捕获全局错误
    window.addEventListener('error', (event) => {
      const targetInfo = this.extractResourceTargetInfo(event.target);
      if (targetInfo.isThirdPartyNoise && !event.error) {
        return;
      }

      this.logError({
        message:
          event.error?.message ||
          (targetInfo.resourceType
            ? `Resource load failed (${targetInfo.resourceType}): ${targetInfo.resourceUrl || 'unknown'}`
            : event.message || 'Global Error'),
        stack: event.error?.stack,
        context: 'global_error',
        timestamp: Date.now(),
        url: window.location.href,
        userAgent: navigator.userAgent,
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
        resourceType: targetInfo.resourceType,
        resourceUrl: targetInfo.resourceUrl,
      });
    });
  }

  private normalizeUnhandledRejectionReason(reason: unknown): {
    message: string;
    stack?: string;
    ignore?: boolean;
    preventDefault?: boolean;
    extra?: Record<string, unknown>;
  } {
    if (reason instanceof Error) {
      return {
        message: reason.message || 'Unhandled Promise Rejection',
        stack: reason.stack,
      };
    }

    // Some browser/library promise rejections reject with an Event object (e.g. <link>/<script> load failures).
    if (reason instanceof Event) {
      const targetInfo = this.extractResourceTargetInfo(reason.target);
      if (targetInfo.resourceType) {
        return {
          message: `Resource promise rejection (${targetInfo.resourceType}): ${targetInfo.resourceUrl || 'unknown'}`,
          preventDefault: true,
          ignore: targetInfo.isThirdPartyNoise,
          extra: {
            reasonType: 'Event',
            eventType: reason.type,
            resourceType: targetInfo.resourceType,
            resourceUrl: targetInfo.resourceUrl,
          },
        };
      }

      return {
        message: `Unhandled Promise Rejection (Event: ${reason.type || 'unknown'})`,
        preventDefault: true,
        extra: {
          reasonType: 'Event',
          eventType: reason.type,
        },
      };
    }

    if (typeof reason === 'string') {
      return {
        message: reason,
      };
    }

    if (reason && typeof reason === 'object' && 'message' in reason) {
      const maybeMessage = (reason as { message?: unknown }).message;
      return {
        message: typeof maybeMessage === 'string' ? maybeMessage : 'Unhandled Promise Rejection',
      };
    }

    return {
      message: 'Unhandled Promise Rejection',
      extra: {
        reasonType: typeof reason,
      },
    };
  }

  private extractResourceTargetInfo(target: EventTarget | null): {
    resourceType?: 'script' | 'link' | 'img';
    resourceUrl?: string;
    isThirdPartyNoise?: boolean;
  } {
    if (typeof window === 'undefined' || !target) {
      return {};
    }

    if (target instanceof HTMLScriptElement) {
      return {
        resourceType: 'script',
        resourceUrl: target.src,
        isThirdPartyNoise: this.isThirdPartyNoiseResource(target.src),
      };
    }

    if (target instanceof HTMLLinkElement) {
      return {
        resourceType: 'link',
        resourceUrl: target.href,
        isThirdPartyNoise: this.isThirdPartyNoiseResource(target.href),
      };
    }

    if (target instanceof HTMLImageElement) {
      return {
        resourceType: 'img',
        resourceUrl: target.src,
        isThirdPartyNoise: this.isThirdPartyNoiseResource(target.src),
      };
    }

    return {};
  }

  private isThirdPartyNoiseResource(url?: string): boolean {
    if (!url) return false;

    try {
      const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
      return this.noisyThirdPartyDomains.some((domain) => parsed.hostname.includes(domain));
    } catch {
      return this.noisyThirdPartyDomains.some((domain) => url.includes(domain));
    }
  }

  /**
   * 记录错误
   */
  public logError(error: ErrorLogEntry): void {
    // 添加到队列
    this.errorQueue.push(error);

    // 限制队列大小
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue.shift();
    }

    // 输出到控制台
    if (!this.isProduction) {
      console.group('🔴 Error Log');
      console.error('Message:', error.message);
      console.error('Context:', error.context || 'unknown');
      if (error.stack) {
        console.error('Stack:', error.stack);
      }
      if (error.componentStack) {
        console.error('Component Stack:', error.componentStack);
      }
      console.groupEnd();
    }

    // 上报到 PostHog
    this.reportToPostHog(error);
  }

  /**
   * 记录 React 错误边界捕获的错误
   */
  public logReactError(error: Error, errorInfo: ErrorInfo, context?: string): void {
    this.logError({
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack || undefined,
      context: context || 'react_error_boundary',
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    });
  }

  /**
   * 记录 API 错误
   */
  public logApiError(
    endpoint: string,
    error: Error,
    requestData?: any,
    responseData?: any
  ): void {
    this.logError({
      message: error.message,
      stack: error.stack,
      context: 'api_error',
      timestamp: Date.now(),
      endpoint,
      requestData: this.sanitizeData(requestData),
      responseData: this.sanitizeData(responseData),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
    });
  }

  /**
   * 上报错误到 PostHog
   */
  private reportToPostHog(error: ErrorLogEntry): void {
    try {
      report(ReportEvent.ERROR_LOGGED, {
        error_message: error.message,
        error_context: error.context,
        error_stack: this.truncateStack(error.stack),
        component_stack: this.truncateStack(error.componentStack),
        url: error.url,
        user_agent: error.userAgent,
        timestamp: error.timestamp,
        ...this.sanitizeData(error),
      });
    } catch (e) {
      console.error('Failed to report error to PostHog:', e);
    }
  }

  /**
   * 截断过长的堆栈信息
   */
  private truncateStack(stack?: string, maxLength = 1000): string | undefined {
    if (!stack) return undefined;
    return stack.length > maxLength ? stack.substring(0, maxLength) + '...' : stack;
  }

  /**
   * 清理敏感数据
   */
  private sanitizeData(data: any): any {
    if (!data) return data;

    const sensitiveKeys = [
      'password',
      'token',
      'apiKey',
      'secret',
      'authorization',
      'cookie',
      'session',
    ];

    if (typeof data === 'object') {
      const sanitized = { ...data };
      Object.keys(sanitized).forEach((key) => {
        if (sensitiveKeys.some((sensitive) => key.toLowerCase().includes(sensitive))) {
          sanitized[key] = '[REDACTED]';
        } else if (typeof sanitized[key] === 'object') {
          sanitized[key] = this.sanitizeData(sanitized[key]);
        }
      });
      return sanitized;
    }

    return data;
  }

  /**
   * 获取错误队列
   */
  public getErrorQueue(): ErrorLogEntry[] {
    return [...this.errorQueue];
  }

  /**
   * 清空错误队列
   */
  public clearErrorQueue(): void {
    this.errorQueue = [];
  }

  /**
   * 导出错误日志（用于调试）
   */
  public exportErrors(): string {
    return JSON.stringify(this.errorQueue, null, 2);
  }
}

// 导出单例实例
export const errorLogger = ErrorLogger.getInstance();

// 导出辅助函数
export const logError = (error: ErrorLogEntry) => errorLogger.logError(error);
export const logReactError = (error: Error, errorInfo: ErrorInfo, context?: string) =>
  errorLogger.logReactError(error, errorInfo, context);
export const logApiError = (
  endpoint: string,
  error: Error,
  requestData?: any,
  responseData?: any
) => errorLogger.logApiError(endpoint, error, requestData, responseData);
